/**
 * iter163h — In-Platform DM context.
 *
 * Holds:
 *   - global unread count (used by the floating button badge)
 *   - WS event subscription to update unread / push new messages to whatever
 *     panel is currently open
 *
 * Exposes a tiny imperative bus the modal can subscribe to.
 */
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
} from 'react';
import { useAuth } from './AuthContext';
import { useWebSocket } from './WebSocketContext';
import { dmApi } from '../lib/api';

const DMContext = createContext(null);

// Roles that are NOT allowed to use DMs. Mirrors backend DM_BLOCKED_ROLES.
const BLOCKED_ROLES = new Set(['COMPANY_REP']);

export const DMProvider = ({ children }) => {
  const { user } = useAuth();
  const { addMessageListener } = useWebSocket();
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [activeConvId, setActiveConvId] = useState(null);
  // iter163i — Persist the composer draft in the context so closing the
  // modal (via Esc, X, or outside-click) and reopening it preserves any
  // half-typed message. Keyed by conversation id.
  const [drafts, setDrafts] = useState({}); // { [convId]: text }
  const getDraft = useCallback((cid) => drafts[cid] || '', [drafts]);
  const setDraft = useCallback((cid, text) => {
    setDrafts((prev) => ({ ...prev, [cid]: text }));
  }, []);
  // "Discuss in DM" deep-link from RequestDetails or other surfaces.
  // Set { user_id, peer_name?, embed? } to open a chat with a pre-attached card.
  const [pendingDM, setPendingDM] = useState(null);
  const startDMWith = useCallback((payload) => {
    setPendingDM(payload);
    setOpen(true);
  }, []);
  const listenersRef = useRef(new Set());

  const eligible = !!user && !BLOCKED_ROLES.has(user?.role);

  const refreshUnread = useCallback(async () => {
    if (!eligible) return;
    try {
      const r = await dmApi.unreadCount();
      setUnread(r.data?.count || 0);
    } catch (_) {
      /* noop */
    }
  }, [eligible]);

  // Initial + periodic refresh (cheap; WebSocket also pushes deltas)
  useEffect(() => {
    if (!eligible) return;
    refreshUnread();
    const t = setInterval(refreshUnread, 60000);
    return () => clearInterval(t);
  }, [eligible, refreshUnread]);

  // Hook into WS events
  useEffect(() => {
    if (!addMessageListener || !eligible) return;
    const unsubscribe = addMessageListener((data) => {
      if (!data || typeof data !== 'object') return;
      const t = data.type;
      if (
        t === 'dm:message:new' ||
        t === 'dm:message:read' ||
        t === 'dm:message:reaction' ||
        t === 'dm:typing' ||
        t === 'dm:status'
      ) {
        // Fan out to subscribers (e.g., open modal)
        listenersRef.current.forEach((cb) => {
          try { cb(data); } catch (e) { console.error('DM listener err:', e); }
        });

        if (t === 'dm:message:new') {
          // The convo the message belongs to (if visible) will mark read itself;
          // otherwise we bump unread.
          const msg = data.message;
          if (msg && msg.sender_id !== user?.id && msg.conversation_id !== activeConvId) {
            setUnread((u) => u + 1);
          }
        }
      }
    });
    return unsubscribe;
  }, [addMessageListener, eligible, user?.id, activeConvId]);

  const subscribe = useCallback((cb) => {
    listenersRef.current.add(cb);
    return () => listenersRef.current.delete(cb);
  }, []);

  return (
    <DMContext.Provider
      value={{
        eligible,
        unread,
        setUnread,
        refreshUnread,
        open,
        setOpen,
        activeConvId,
        setActiveConvId,
        subscribe,
        getDraft,
        setDraft,
        pendingDM,
        setPendingDM,
        startDMWith,
      }}
    >
      {children}
    </DMContext.Provider>
  );
};

export const useDM = () => {
  const ctx = useContext(DMContext);
  if (!ctx) throw new Error('useDM must be used within DMProvider');
  return ctx;
};
